import React from 'react';
import Icon from '../../../components/AppIcon';

const ClientProfileHeader = ({ client }) => {
  const getBalanceStatus = (balance) => {
    if (balance === 0) return { color: 'text-muted-foreground', bgColor: 'bg-muted-foreground', status: 'Empty' };
    if (balance < 30) return { color: 'text-warning', bgColor: 'bg-warning', status: 'Low' };
    if (balance < 60) return { color: 'text-accent', bgColor: 'bg-accent', status: 'Medium' };
    return { color: 'text-success', bgColor: 'bg-success', status: 'Good' };
  };

  const getMembershipColor = (type) => {
    switch (type) {
      case 'VIP': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Premium': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Standard': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const balanceInfo = getBalanceStatus(client?.balance || 0);
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 px-6 py-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {client?.name}
            </h1>
            <div className="flex items-center space-x-4 text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} />
                <span>{client?.phone ? client.phone.replace(/\D/g, '') : ''}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} />
                <span>{client?.email}</span>
              </div>
            </div>
          </div>
          
          {/* Balance Display */}
          <div className="text-right">
            <div className="bg-card rounded-lg p-4 border border-border shadow-sm">
              <div className={`text-4xl font-bold font-mono ${balanceInfo.color} mb-1`}>
                {client?.balance || 0}
              </div>
              <div className="text-sm text-muted-foreground mb-2">minutes</div>
              <div className="flex items-center justify-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${balanceInfo.bgColor}`}></div>
                <span className={`text-xs font-medium ${balanceInfo.color}`}>
                  {balanceInfo.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="User" size={20} />
              <span>Personal Information</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Age:</span>
                <span className="font-medium">{calculateAge(client?.birthDate)} years old</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Membership:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getMembershipColor(client?.membershipType)}`}>
                  {client?.membershipType}
                </span>
              </div>
              <div className="flex items-start justify-between py-2">
                <span className="text-muted-foreground">Address:</span>
                <span className="font-medium text-right max-w-[200px]">{client?.address}</span>
              </div>
            </div>
          </div>

          {/* Account Statistics */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="BarChart3" size={20} />
              <span>Account Statistics</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Member Since:</span>
                <span className="font-medium">{formatDate(client?.joinDate)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-muted-foreground">Last Visit:</span>
                <span className="font-medium">{formatDate(client?.lastVisit)}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-muted-foreground">Total Purchased:</span>
                <span className="font-medium text-success">{client?.totalPurchased} minutes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfileHeader;